import * as React from 'react';
import { Scope } from '../../types/roles';
import { useCallback, useMemo } from 'react';
import * as s from './RoleHelpers.scss';
import { translate } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getScopeSelectedDependencies } from '../../redux/selectors/areas';
import { State } from '../../redux/configure-store';
import StatusAlertFilledSmall from 'wix-ui-icons-common/StatusAlertFilledSmall';

import {
  Text,
  Dropdown,
  ToggleSwitch,
  Box,
  Checkbox,
  EmptyState,
  TextButton,
  Heading,
} from 'wix-style-react';

export const countAreaSelectedScopes = (scopes: Scope[], selectedScopes: string[]) =>
    scopes.reduce((acc, scope) => selectedScopes.includes(scope.id) ? acc + 1 : acc, 0);

export const buildAreaTitle = (title, shouldDisplayCounter, scopes, selectedScopes) => {
  const areaSelectedScopes = countAreaSelectedScopes(scopes, selectedScopes);
  return (
    <Text weight="normal">
      { `${title} ` }
      { shouldDisplayCounter && !!areaSelectedScopes && <Text secondary light>({areaSelectedScopes})</Text> }
    </Text>
  );
};

export const buildPermissionsHeader = (t, shouldDisplayCounter, selectedScopes) => (
  <Heading appearance="H3">
    { `${t('role.permissions')} ` }
    { shouldDisplayCounter && !!selectedScopes.length && <Text secondary light>({selectedScopes.length})</Text> }
  </Heading>
);

interface IScopesByGroup {
  scopes: Scope[];
  readOnly: boolean;
  selectedScopes: string[];
  disabledScopes: string[];

  onScopeUpdate(scope: string): void;
  onAreaSelectAllUpdate(scopeIds: string[]): void;
}

export const ScopesByGroup: React.FC<IScopesByGroup> = ({ scopes, onScopeUpdate, readOnly, selectedScopes, disabledScopes , onAreaSelectAllUpdate }) => (
  <React.Fragment>
    { !readOnly && <SelectAllRow
      areaScopes={scopes}
      selectedScopes={selectedScopes}
      disabled={scopes.filter(scope => disabledScopes.includes(scope.id)).length === scopes.length }
      onAreaSelectAllUpdate={onAreaSelectAllUpdate}/>
    }
    {
      scopes.map((scope, scopeIndex) => (
        <ScopeRow
          key={scope.id}
          scope={scope}
          onScopeUpdate={onScopeUpdate}
          selected={selectedScopes.includes(scope.id)}
          disabled={readOnly || disabledScopes.includes(scope.id)}
          selectedScopes={selectedScopes}
        />
      ))
    }
  </React.Fragment>
);

const SelectAllRow = translate()(({ areaScopes, selectedScopes, disabled, onAreaSelectAllUpdate, t }: { areaScopes: Scope[], selectedScopes: string[], disabled: boolean, onAreaSelectAllUpdate(scopeIds: string[]): void; t: any}) => {

  const areaSelectedScope = useMemo<number>(() => (countAreaSelectedScopes(areaScopes, selectedScopes)), [areaScopes, selectedScopes]);

  const checked = useMemo<boolean>(() => (areaSelectedScope === areaScopes.length), [areaSelectedScope, areaScopes]);

  const scopeIds = useMemo<string[]>(() => areaScopes.map(areaScope => areaScope.id), [areaScopes]);

  const onSelectAllClick = useCallback(() => onAreaSelectAllUpdate(scopeIds), [scopeIds, onAreaSelectAllUpdate]);

  return (
    <Box verticalAlign="top" cursor={disabled ? 'default' : 'pointer'}>
      <Checkbox
        className={s.selectAllCheckbox}
        dataHook={`rolePermissions-checkbox-${scopeIds.join('-')}-selectAll`}
        vAlign="top"
        onChange={onSelectAllClick}
        checked={checked}
        indeterminate={!checked && areaSelectedScope > 0}
        disabled={disabled && checked}/>
      <Box
        className={s.SelectAllDivider}
        marginLeft="medium"
        paddingBottom="18px"
        width="100%">
        <Text size="small" weight="normal" onClick={!disabled ? onSelectAllClick : undefined} secondary>{t('role.selectAll')}</Text>
      </Box>
    </Box>
  );
});

const ScopeRow = translate()(({ t, scope, onScopeUpdate, selected, disabled, selectedScopes }: { t, scope: Scope, onScopeUpdate(scopeId): void, selected: boolean, disabled: boolean, selectedScopes: string[] }) => {

  const dependencyForScopes = useSelector((state: State) => getScopeSelectedDependencies(state, selectedScopes, scope.id));

  const getRoleDependenciesText = useMemo(() => {
    if (dependencyForScopes.length === 0) {
      return false;
    } else {
      const text =  t('role.permissionRequired.singular', { roleTitle: dependencyForScopes[0].title });
      return <Box verticalAlign="middle" marginTop="6px"><StatusAlertFilledSmall style={{ fill: '#fdb10c', marginRight: '6px' }}/><Text size="small" weight="thin" secondary>{text}</Text></Box>;
    }
   }, [t, dependencyForScopes]);

  const onRowUpdate = useCallback(() => {
    if (!disabled) {
      onScopeUpdate(scope.id);
    }
  }, [scope, onScopeUpdate, disabled]);

  return (
    <Box cursor={disabled ? 'default' : 'pointer'} verticalAlign="middle" height={ !!getRoleDependenciesText ? '96px' : '72px' }>
      <Box>
        <Checkbox
          dataHook={'rolePermissions-checkbox'}
          vAlign="top"
          onChange={onRowUpdate}
          checked={selected}
          disabled={disabled}
        />
        <Box
          marginLeft="medium"
          direction="vertical"
          width="100%">
          <Text size="medium"  weight="normal" onClick={onRowUpdate} secondary>{scope.title}</Text>
          <Text size="small" weight="thin" onClick={onRowUpdate} secondary>
            {scope.description}
          </Text>
          { getRoleDependenciesText }
        </Box>
      </Box>
    </Box>
  );
});

export const PermissionsEmptyState = (t, onShowAllClick) => (
  <EmptyState
    title={t('role.permissions.emptyState.title')}
    subtitle={<Text>
      {t('role.permissions.emptyState.subtitle')} <TextButton onClick={onShowAllClick}>
        {t('role.permissions.emptyState.showAll')}
      </TextButton>
    </Text>}
    theme="page-no-border"/>
);

const ScopeToggle = ({ scope, selected, onScopeUpdate, readOnly }) => (
    <ToggleSwitch
        disabled={readOnly}
        checked={selected}
        dataHook={'rolePermissions-toggle'}
        onChange={() => onScopeUpdate(scope.id)}/>
);

const ScopeDropdown = ({ selected, onScopeUpdate, areaId, scopeId, values, readOnly }) => (
    <Dropdown
        initialSelectedId={selected}
        disabled={readOnly}
        dataHook={'rolePermissions-dropdown'}
        onSelect={({ id }) => onScopeUpdate({ areaId, scopeId, selected: id })}
        options={values.map(({ id, name }) => ({
                id,
                value: name
            })
        )}
    />
);
